import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Loader2, Plus, MoreVertical, User, Calendar, MessageSquare, Clock, Phone, Mail, Globe, Send, Filter, X, Users, RefreshCw, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { DateRangePicker } from '@/components/ui/date-range-picker';

// Helper function to convert unix timestamp to Date
const convertUnixToDate = (timestamp) => {
  if (!timestamp) return null;
  const dateInMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  return new Date(dateInMs);
};

// Helper function to format date for display
const formatDisplayDate = (timestamp) => {
  const date = convertUnixToDate(timestamp);
  if (!date) return 'N/A';
  try {
    return format(date, 'MMM dd, yyyy');
  } catch {
    return 'N/A';
  }
};

// Helper function to calculate time in queue
const calculateTimeInQueue = (createdAtTimestamp) => {
  if (!createdAtTimestamp) return 'N/A';
  
  const now = Date.now();
  const createdAt = createdAtTimestamp < 10000000000 ? createdAtTimestamp * 1000 : createdAtTimestamp;
  const diffMs = now - createdAt;
  
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else {
    return `${minutes}m`;
  }
};

// Status configuration with exact expected statuses and icons
const statusConfig = {
  'to do': {
    color: '#9333EA', // Purple
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-600',
    dotColor: 'bg-purple-600',
    icon: '📋'
  },
  'in progress': {
    color: '#3B82F6', // Blue
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-600',
    dotColor: 'bg-blue-600',
    icon: '⚡'
  },
  'needs attention': {
    color: '#EF4444', // Red
    bgColor: 'bg-red-50',
    borderColor: 'border-red-600',
    dotColor: 'bg-red-600',
    icon: '⚠️'
  },
  'completed': {
    color: '#10B981', // Green
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-600',
    dotColor: 'bg-emerald-600',
    icon: '✅'
  },
  'completed - incomplete': {
    color: '#F59E0B', // Amber/Orange
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-600',
    dotColor: 'bg-amber-600',
    icon: '⚙️'
  }
};

// Priority color mapping
const getPriorityBadgeColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return 'bg-rose-100 text-rose-700';
    case 'intermediate':
      return 'bg-amber-100 text-amber-700';
    case 'low':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-neutral-100 text-neutral-600';
  }
};

export default function SupportTickets({ userInfo }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showSlidePanel, setShowSlidePanel] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [ticketToAssign, setTicketToAssign] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [ticketToEdit, setTicketToEdit] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [ticketToReassign, setTicketToReassign] = useState(null);
  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [draggedTicket, setDraggedTicket] = useState(null);
  
  // Filter states
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedCreatedBy, setSelectedCreatedBy] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [createdBySearch, setCreatedBySearch] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');

  // Check if user has Admin permission
  const userPermissionsArray = userInfo?.permissions || [];
  const userPermissions = userPermissionsArray.map(item => 
    item?.user_permission?.permission || item?.permission
  ).filter(Boolean);
  const hasAdminPermission = userPermissions.includes('Admin');

  // Calculate urgency highlighting for tickets
  const getTicketUrgency = (ticket) => {
    // Only highlight tickets in "To Do" or "In Progress" status
    const ticketStatus = ticket.status?.toLowerCase();
    if (ticketStatus !== 'to do' && ticketStatus !== 'in progress') return null;
    
    // Skip if no ticket_reference
    if (!ticket.ticket_reference) return null;
    
    const currentRef = parseInt(ticket.ticket_reference);
    if (isNaN(currentRef)) return null;
    
    // Check if any ticket with higher reference is in progress or completed
    let hasHigherInProgress = false;
    let hasHigherCompleted = false;
    
    tickets.forEach(t => {
      const tRef = parseInt(t.ticket_reference);
      if (!isNaN(tRef) && tRef > currentRef) {
        if (t.status?.toLowerCase() === 'in progress') {
          hasHigherInProgress = true;
        }
        if (t.status?.toLowerCase() === 'completed') {
          hasHigherCompleted = true;
        }
      }
    });
    
    // Return urgency level based on ticket status
    if (ticketStatus === 'to do') {
      // For "To Do" tickets
      if (hasHigherCompleted) return 'critical'; // Red gradient
      if (hasHigherInProgress) return 'warning';  // Orange gradient
    } else if (ticketStatus === 'in progress') {
      // For "In Progress" tickets
      if (hasHigherCompleted) return 'critical'; // Red gradient if newer ticket is done
    }
    
    return null;
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError('');
      
      // All users now use the same endpoint
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets');
      
      const ticketsData = response.data || [];
      setTickets(ticketsData);
    } catch (err) {
      console.error('Error fetching support tickets:', err);
      setError('Failed to load support tickets. Please try again.');
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimTicket = async (ticketId) => {
    try {
      setClaiming(true);
      const userId = userInfo?.ghl_userid || userInfo?.user_id;
      
      if (!userId) {
        throw new Error('User ID not found');
      }

      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets/claim', {
        ticket_id: ticketId,
        user_id: userId
      });

      toast.success('Ticket claimed successfully');
      await fetchTickets();
    } catch (err) {
      console.error('Error claiming ticket:', err);
      toast.error('Failed to claim ticket');
    } finally {
      setClaiming(false);
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowSlidePanel(true);
  };

  const handleClosePanel = () => {
    setShowSlidePanel(false);
    setTimeout(() => setSelectedTicket(null), 300);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedTicket(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    
    if (!draggedTicket) return;
    
    // Don't do anything if dropped in the same column
    if (draggedTicket.status?.toLowerCase() === newStatus) {
      setDraggedTicket(null);
      return;
    }

    try {
      const userId = userInfo?.user_id;
      if (!userId) {
        throw new Error('User ID not found');
      }

      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets/update/status', {
        user_id: userId,
        ticket_id: draggedTicket.id,
        status: newStatus
      });

      toast.success('Ticket status updated successfully');
      await fetchTickets();
    } catch (err) {
      console.error('Error updating ticket status:', err);
      toast.error('Failed to update ticket status');
    } finally {
      setDraggedTicket(null);
    }
  };

  // Group tickets by status in the exact order and apply filters
  const { stages, totalTickets, totalInPipe, totalCompleted, summaryByStatus, summaryByPriority, summaryByAssignedUser, filteredTickets, uniqueCreatedBy, uniqueAssignees } = useMemo(() => {
    const orderedStatuses = ['to do', 'in progress', 'needs attention', 'completed', 'completed - incomplete'];
    
    // Get unique values for filters
    const createdBySet = new Set();
    const assigneeSet = new Set();
    
    tickets.forEach(ticket => {
      if (ticket.createdBy_name) createdBySet.add(ticket.createdBy_name);
      if (ticket.assignedTo_name) assigneeSet.add(ticket.assignedTo_name);
    });
    
    // Apply filters
    let filtered = [...tickets];
    
    if (dateRange.from && dateRange.to) {
      filtered = filtered.filter(t => {
        if (!t.created_at) return false;
        const ticketDate = new Date(t.created_at < 10000000000 ? t.created_at * 1000 : t.created_at);
        return ticketDate >= dateRange.from && ticketDate <= dateRange.to;
      });
    }
    
    // Filter by Created By
    if (selectedCreatedBy.length > 0) {
      filtered = filtered.filter(t => selectedCreatedBy.includes(t.createdBy_name));
    }
    
    // Filter by Assignee
    if (selectedAssignees.length > 0) {
      filtered = filtered.filter(t => selectedAssignees.includes(t.assignedTo_name));
    }
    
    const stageData = orderedStatuses.map(status => {
      const stageTickets = filtered.filter(t => t.status?.toLowerCase() === status);
      return {
        status,
        displayName: status.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        count: stageTickets.length,
        tickets: stageTickets,
        config: statusConfig[status]
      };
    });
    
    // Calculate summaries from all tickets (not filtered)
    const statusSummary = {};
    const prioritySummary = {};
    const assignedSummary = {};
    
    tickets.forEach(ticket => {
      // Status summary
      const status = ticket.status?.toLowerCase() || 'unknown';
      statusSummary[status] = (statusSummary[status] || 0) + 1;
      
      // Priority summary
      const priority = ticket.priority?.toLowerCase() || 'unknown';
      prioritySummary[priority] = (prioritySummary[priority] || 0) + 1;
      
      // Assigned user summary
      const assignedTo = ticket.assignedTo_name || 'Unassigned';
      assignedSummary[assignedTo] = (assignedSummary[assignedTo] || 0) + 1;
    });
    
    const completed = tickets.filter(t => t.status?.toLowerCase() === 'completed').length;
    const inPipe = tickets.length - completed;
    
    return {
      stages: stageData,
      totalTickets: filtered.length,
      totalInPipe: inPipe,
      totalCompleted: completed,
      summaryByStatus: statusSummary,
      summaryByPriority: prioritySummary,
      summaryByAssignedUser: assignedSummary,
      filteredTickets: filtered,
      uniqueCreatedBy: Array.from(createdBySet).sort(),
      uniqueAssignees: Array.from(assigneeSet).sort()
    };
  }, [tickets, dateRange, selectedCreatedBy, selectedAssignees]);
  
  // Get unique values for filters
  const uniqueStatuses = [...new Set(tickets.map(t => t.status?.toLowerCase()).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchTickets}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Support Tickets</h1>
          <p className="text-sm text-neutral-500 mt-1">{filteredTickets.length} of {tickets.length} tickets</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTickets}
            disabled={loading}
            className="rounded-lg"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddTicketModal(true)}
            className="rounded-lg"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Ticket
          </Button>
        </div>
      </div>

      {/* Summary Dashboard - Matches column widths */}
      <div className="flex gap-4">
        {/* Summary by Status - 50% width */}
        <Card className="p-4 w-1/2">
          <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">By Status</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(summaryByStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${statusConfig[status]?.dotColor || 'bg-neutral-400'}`}></div>
                  <span className="capitalize text-neutral-700 text-xs">{status}</span>
                </div>
                <span className="font-semibold text-neutral-900 text-sm">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Summary by Priority - 25% width */}
        <Card className="p-4 w-1/4">
          <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">By Priority</h3>
          <div className="space-y-2">
            {Object.entries(summaryByPriority).map(([priority, count]) => (
              <div key={priority} className="flex items-center justify-between text-sm">
                <span className="capitalize text-neutral-700 text-xs">{priority}</span>
                <span className="font-semibold text-neutral-900 text-sm">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Summary by Assigned User - 25% width - Now visible to all users */}
        <Card className="p-4 w-1/4">
          <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">By Assigned User</h3>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {Object.entries(summaryByAssignedUser).slice(0, 5).map(([user, count]) => (
              <div key={user} className="flex items-center justify-between text-sm">
                <span className="text-neutral-700 truncate text-xs">{user}</span>
                <span className="font-semibold text-neutral-900 text-sm">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Filters - Icon Style like My Tickets */}
      <div className="flex items-center gap-2">
        {/* Date Range Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`rounded-xl h-9 w-9 ${(dateRange.from && dateRange.to) ? 'border-[#d4af37] bg-[#f4e5b8]' : ''}`}
            >
              <Calendar className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-neutral-700">Date Created</p>
                {dateRange.from && dateRange.to && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange({ from: null, to: null })}
                    className="h-6 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
            </div>
          </PopoverContent>
        </Popover>

        {/* Created By Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`rounded-xl h-9 w-9 ${selectedCreatedBy.length > 0 ? 'border-[#d4af37] bg-[#f4e5b8]' : ''}`}
            >
              <User className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-neutral-700">Created By</p>
                {selectedCreatedBy.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCreatedBy([])}
                    className="h-6 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Search Input */}
              <Input
                placeholder="Search creators..."
                value={createdBySearch}
                onChange={(e) => setCreatedBySearch(e.target.value)}
                className="h-8 text-sm"
              />
              
              {/* Checkbox List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {uniqueCreatedBy
                  .filter(name => name.toLowerCase().includes(createdBySearch.toLowerCase()))
                  .map(name => (
                    <div key={name} className="flex items-center space-x-2">
                      <Checkbox
                        id={`created-${name}`}
                        checked={selectedCreatedBy.includes(name)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedCreatedBy([...selectedCreatedBy, name]);
                          } else {
                            setSelectedCreatedBy(selectedCreatedBy.filter(n => n !== name));
                          }
                        }}
                      />
                      <label
                        htmlFor={`created-${name}`}
                        className="text-sm text-neutral-700 cursor-pointer flex-1"
                      >
                        {name}
                      </label>
                    </div>
                  ))}
              </div>
              
              {selectedCreatedBy.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-neutral-500">{selectedCreatedBy.length} selected</p>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Assignee Filter - Now visible to all users */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`rounded-xl h-9 w-9 ${selectedAssignees.length > 0 ? 'border-[#d4af37] bg-[#f4e5b8]' : ''}`}
            >
              <Users className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-neutral-700">Assigned To</p>
                {selectedAssignees.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedAssignees([])}
                    className="h-6 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Search Input */}
              <Input
                placeholder="Search assignees..."
                value={assigneeSearch}
                onChange={(e) => setAssigneeSearch(e.target.value)}
                className="h-8 text-sm"
              />
              
              {/* Checkbox List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {uniqueAssignees
                  .filter(name => name.toLowerCase().includes(assigneeSearch.toLowerCase()))
                  .map(name => (
                    <div key={name} className="flex items-center space-x-2">
                      <Checkbox
                        id={`assignee-${name}`}
                        checked={selectedAssignees.includes(name)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAssignees([...selectedAssignees, name]);
                          } else {
                            setSelectedAssignees(selectedAssignees.filter(n => n !== name));
                          }
                        }}
                      />
                      <label
                        htmlFor={`assignee-${name}`}
                        className="text-sm text-neutral-700 cursor-pointer flex-1"
                      >
                        {name}
                      </label>
                    </div>
                  ))}
              </div>
              
              {selectedAssignees.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-neutral-500">{selectedAssignees.length} selected</p>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Clear All Filters */}
        {((dateRange.from && dateRange.to) || selectedCreatedBy.length > 0 || selectedAssignees.length > 0) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDateRange({ from: null, to: null });
              setSelectedCreatedBy([]);
              setSelectedAssignees([]);
            }}
            className="text-xs text-neutral-500 hover:text-neutral-700 h-9"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Pipeline Stages */}
      <div className="grid grid-cols-5 gap-3">
        {stages.length === 0 ? (
          <div className="col-span-5 text-center py-20 bg-white rounded-2xl border border-neutral-200 shadow-sm">
            <div className="w-16 h-16 bg-neutral-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-neutral-300" />
            </div>
            <p className="text-neutral-400 text-sm">No tickets found</p>
          </div>
        ) : (
          stages.map((stage) => (
            <div
              key={stage.status}
              className="min-w-0"
            >
              {/* Column Container */}
              <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden h-full">
                {/* Column Header */}
                <div className={`px-4 py-3 border-b-4 ${stage.config.borderColor} bg-white`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{stage.config.icon}</span>
                    <div className={`w-2.5 h-2.5 rounded-full ${stage.config.dotColor}`}></div>
                    <h3 className="font-medium text-neutral-700 capitalize text-sm">
                      {stage.displayName}
                    </h3>
                    <span className="ml-auto text-xs text-neutral-400 font-medium">
                      {stage.count}
                    </span>
                  </div>
                </div>

                {/* Cards Container - Drop Zone */}
                <div 
                  className={`p-3 space-y-2 ${stage.config.bgColor} max-h-[calc(100vh-280px)] overflow-y-auto transition-colors ${
                    draggedTicket && draggedTicket.status?.toLowerCase() !== stage.status 
                      ? 'ring-2 ring-[#d4af37] ring-inset bg-[#f4e5b8]/20' 
                      : ''
                  }`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.status)}
                >
                  {stage.tickets.map((ticket) => {
                    const urgency = getTicketUrgency(ticket);
                    const urgencyStyles = urgency === 'critical' 
                      ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 shadow-red-100'
                      : urgency === 'warning'
                      ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300 shadow-orange-100'
                      : 'bg-white';
                    
                    return (
                      <Card
                        key={ticket.id}
                        draggable={hasAdminPermission || (ticket.assignedTo_user_id && userInfo?.user_id && ticket.assignedTo_user_id === userInfo.user_id)}
                        onDragStart={(e) => handleDragStart(e, ticket)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleTicketClick(ticket)}
                        className={`${urgencyStyles} p-4 hover:shadow-xl transition-all duration-200 ${
                          (hasAdminPermission || (ticket.assignedTo_user_id && userInfo?.user_id && ticket.assignedTo_user_id === userInfo.user_id)) ? 'cursor-move' : 'cursor-pointer'
                        } bg-white border-2 border-neutral-200 rounded-2xl relative ${
                          draggedTicket?.id === ticket.id ? 'opacity-50 scale-95' : ''
                        } ${urgency ? 'ring-2 ' + (urgency === 'critical' ? 'ring-red-400/50' : 'ring-orange-400/50') : ''} mb-3`}
                      >
                      {/* Priority Badge and Edit Icon - Top Right */}
                      <div className="absolute top-3 right-3 flex items-center gap-1.5">
                        {ticket.priority && (
                          <Badge className={`text-[9px] px-2 py-0.5 rounded-lg font-semibold ${getPriorityBadgeColor(ticket.priority)}`}>
                            {ticket.priority}
                          </Badge>
                        )}
                        
                        {/* Edit Icon - Available to Admin or assigned user */}
                        {(hasAdminPermission || (ticket.assignedTo_user_id && userInfo?.user_id && ticket.assignedTo_user_id === userInfo.user_id)) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTicketToEdit(ticket);
                              setShowEditModal(true);
                            }}
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 hover:bg-neutral-100 rounded-lg"
                          >
                            <Pencil className="w-3.5 h-3.5 text-neutral-600" />
                          </Button>
                        )}
                      </div>

                      {/* Ticket Icon and Name */}
                      <div className="flex items-start gap-2.5 mb-2.5 pr-20">
                        <div className="w-9 h-9 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                          <span className="text-[11px] font-bold text-neutral-700">
                            {ticket.name?.substring(0, 2).toUpperCase() || 'TK'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Ticket Reference and Creator on same line */}
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            {ticket.ticket_reference && (
                              <span className="text-[9px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md font-mono font-semibold">
                                #{ticket.ticket_reference}
                              </span>
                            )}
                            {ticket.createdBy_name && (
                              <span className="text-[10px] text-neutral-500 font-medium">by {ticket.createdBy_name}</span>
                            )}
                          </div>
                          {/* Title */}
                          <h4 className="font-bold text-[13px] text-neutral-800 line-clamp-2 leading-snug">
                            {ticket.name || 'Untitled Ticket'}
                          </h4>
                        </div>
                      </div>

                      {/* Description if available */}
                      {ticket.description && (
                        <p className="text-[11px] text-neutral-600 mb-2.5 line-clamp-2 leading-relaxed ml-11">
                          {ticket.description}
                        </p>
                      )}

                      {/* Date and Time in Queue - Inline */}
                      <div className="flex items-center gap-3 text-[10px] mb-2.5 ml-11 flex-wrap">
                        {ticket.created_at && (
                          <div className="flex items-center gap-1.5 text-neutral-500">
                            <Calendar className="w-3.5 h-3.5" />
                            <span className="font-medium">{formatDisplayDate(ticket.created_at)}</span>
                          </div>
                        )}
                        {ticket.created_at && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-neutral-400" />
                            <span className="font-semibold text-neutral-700">{calculateTimeInQueue(ticket.created_at)}</span>
                            <span className="text-neutral-400">in queue</span>
                          </div>
                        )}
                      </div>

                      {/* Divider - Single */}
                      <div className="border-t border-neutral-100 my-2.5"></div>

                      {/* Footer - Compact but clear */}
                      <div className="flex items-center justify-between pt-2.5">
                        {/* Assigned To or Action Buttons */}
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          {(ticket.assignedTo_name && ticket.assignedTo_id) ? (
                            <>
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#d4af37] to-[#b8941e] flex items-center justify-center flex-shrink-0 shadow-sm">
                                <span className="text-white text-[10px] font-bold">
                                  {ticket.assignedTo_name?.split(' ').map(n => n[0]).join('') || 'A'}
                                </span>
                              </div>
                              <span className="text-[11px] text-neutral-700 font-semibold truncate">{ticket.assignedTo_name}</span>
                              
                              {/* Reassign button - Available to all users with color */}
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTicketToReassign(ticket);
                                  setShowReassignModal(true);
                                }}
                                size="sm"
                                variant="outline"
                                className="text-[10px] h-6 px-2.5 rounded-lg border-purple-400 text-purple-600 hover:bg-purple-500 hover:text-white transition-colors ml-1"
                              >
                                Reassign
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* For Non-Admin: Only Claim button */}
                              {!hasAdminPermission && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClaimTicket(ticket.id);
                                  }}
                                  disabled={claiming}
                                  size="sm"
                                  variant="outline"
                                  className="text-[10px] h-6 px-2.5 rounded-lg border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-white font-semibold"
                                >
                                  {claiming ? 'Claiming...' : 'Claim'}
                                </Button>
                              )}
                              
                              {/* For Admin: Both Assign and Claim buttons */}
                              {hasAdminPermission && (
                                <>
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTicketToAssign(ticket);
                                      setShowAssignModal(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    className="text-[10px] h-6 px-2.5 rounded-lg border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-white font-semibold"
                                  >
                                    Assign
                                  </Button>
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClaimTicket(ticket.id);
                                    }}
                                    disabled={claiming}
                                    size="sm"
                                    variant="outline"
                                    className="text-[10px] h-6 px-2.5 rounded-lg border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white font-semibold"
                                  >
                                    {claiming ? 'Claiming...' : 'Claim'}
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </div>

                        {/* Comment Count */}
                        {ticket.comment && ticket.comment.length > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px] text-neutral-600 bg-neutral-50 px-2 py-1 rounded-lg">
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span className="font-semibold">{ticket.comment.length}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Slide Panel */}
      {showSlidePanel && selectedTicket && (
        <TicketDetailPanel
          ticket={selectedTicket}
          onClose={handleClosePanel}
          onRefresh={fetchTickets}
          userInfo={userInfo}
        />
      )}

      {/* Assign Modal */}
      {showAssignModal && ticketToAssign && (
        <AssignTicketModal
          ticket={ticketToAssign}
          onClose={() => {
            setShowAssignModal(false);
            setTicketToAssign(null);
          }}
          onAssign={async (assignedToUserId) => {
            try {
              await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets/assign', {
                ticket_id: ticketToAssign.id,
                user_id: assignedToUserId,
                user_userid: userInfo?.user_id
              });
              
              toast.success('Ticket assigned successfully');
              await fetchTickets();
              setShowAssignModal(false);
              setTicketToAssign(null);
            } catch (err) {
              console.error('Error assigning ticket:', err);
              toast.error('Failed to assign ticket');
            }
          }}
        />
      )}

      {/* Edit Ticket Modal */}
      {showEditModal && ticketToEdit && (
        <EditTicketModal
          ticket={ticketToEdit}
          userInfo={userInfo}
          onClose={() => {
            setShowEditModal(false);
            setTicketToEdit(null);
          }}
          onSave={async (updates) => {
            try {
              const userId = userInfo?.user_id;
              if (!userId) {
                throw new Error('User ID not found');
              }

              await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets/update', {
                user_id: userId,
                ticket_id: ticketToEdit.id,
                priority: updates.priority,
                createdBy_id: updates.createdBy_id
              });

              toast.success('Ticket updated successfully');
              await fetchTickets();
              setShowEditModal(false);
              setTicketToEdit(null);
            } catch (err) {
              console.error('Error updating ticket:', err);
              toast.error('Failed to update ticket');
            }
          }}
        />
      )}

      {/* Reassign Modal */}
      {showReassignModal && ticketToReassign && (
        <ReassignTicketModal
          ticket={ticketToReassign}
          userInfo={userInfo}
          onClose={() => {
            setShowReassignModal(false);
            setTicketToReassign(null);
          }}
          onReassign={async (newAssigneeId, comment) => {
            try {
              const userId = userInfo?.user_id;
              if (!userId) {
                throw new Error('User ID not found');
              }

              await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets/reassign', {
                user_id: userId,
                ticket_id: ticketToReassign.id,
                new_assignee_id: newAssigneeId,
                comment: comment
              });
              
              toast.success('Ticket reassigned successfully');
              await fetchTickets();
              setShowReassignModal(false);
              setTicketToReassign(null);
            } catch (err) {
              console.error('Error reassigning ticket:', err);
              toast.error('Failed to reassign ticket');
            }
          }}
        />
      )}

      {/* Add Ticket Modal */}
      {showAddTicketModal && (
        <AddTicketModal
          userInfo={userInfo}
          onClose={() => setShowAddTicketModal(false)}
          onAdd={async (ticketData) => {
            try {
              const userId = userInfo?.user_id;
              if (!userId) {
                throw new Error('User ID not found');
              }

              await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/tickets/staff/add', {
                user_id: userId,
                ...ticketData
              });
              
              toast.success('Ticket created successfully');
              await fetchTickets();
              setShowAddTicketModal(false);
            } catch (err) {
              console.error('Error creating ticket:', err);
              toast.error('Failed to create ticket');
            }
          }}
        />
      )}
    </div>
  );
}

// Ticket Detail Slide Panel Component
function TicketDetailPanel({ ticket: initialTicket, onClose, onRefresh, userInfo }) {
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [ticket, setTicket] = useState(initialTicket);

  // Check if user has Admin permission
  const userPermissionsArray = userInfo?.permissions || [];
  const userPermissions = userPermissionsArray.map(item => 
    item?.user_permission?.permission || item?.permission
  ).filter(Boolean);
  const hasAdminPermission = userPermissions.includes('Admin');

  // Check if user can comment on this ticket
  // Everyone can comment on any ticket
  const canComment = true;

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setAddingComment(true);
      
      const userId = userInfo?.user_id;
      if (!userId) {
        throw new Error('User ID not found');
      }

      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/mytickets/comment/add', {
        user_id: userId,
        ticket_id: ticket.id,
        message: newComment.trim()
      });

      toast.success('Comment added successfully');
      
      // Optimistically add the new comment to the UI
      const newCommentObj = {
        message: newComment.trim(),
        created_at: Date.now(),
        _commentby: {
          first_name: userInfo?.first_name || 'You',
          last_name: userInfo?.last_name || ''
        }
      };
      
      setTicket(prev => ({
        ...prev,
        comment: [...(prev.comment || []), newCommentObj]
      }));
      
      setNewComment('');
      // Refresh in background without closing the modal
      onRefresh();
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Slide Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[500px] bg-white shadow-2xl z-50 animate-in slide-in-from-right duration-300 flex flex-col">
        {/* Header - Compact */}
        <div className="px-5 py-3 border-b border-neutral-200 bg-gradient-to-r from-[#f4e5b8]/20 to-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-base font-bold text-neutral-900 line-clamp-2 leading-tight">
                  {ticket.name || 'Untitled Ticket'}
                </h2>
                {ticket.ticket_reference && (
                  <span className="text-[10px] px-2 py-0.5 bg-neutral-200 text-neutral-700 rounded font-mono font-semibold shrink-0">
                    #{ticket.ticket_reference}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {ticket.status && (
                  <Badge className="text-[9px] px-1.5 py-0.5 rounded font-medium uppercase bg-neutral-100 text-neutral-600">
                    {ticket.status}
                  </Badge>
                )}
                {ticket.priority && (
                  <Badge className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase ${getPriorityBadgeColor(ticket.priority)}`}>
                    {ticket.priority}
                  </Badge>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Ticket Info - Compact Grid */}
          <div className="p-5 space-y-4 border-b border-neutral-100">
            {/* Description */}
            {ticket.description && (
              <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-100">
                <p className="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {ticket.description}
                </p>
              </div>
            )}

            {/* Metadata - Compact 3-column grid */}
            <div className="grid grid-cols-3 gap-3 text-[11px]">
              <div className="space-y-0.5">
                <p className="text-neutral-400 uppercase tracking-wide font-semibold">Created By</p>
                <p className="text-neutral-900 font-medium">{ticket.createdBy_name || 'Unknown'}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-neutral-400 uppercase tracking-wide font-semibold">Assigned To</p>
                <p className="text-neutral-900 font-medium">{ticket.assignedTo_name || 'Unassigned'}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-neutral-400 uppercase tracking-wide font-semibold">Time in Queue</p>
                <p className="text-neutral-900 font-semibold">{calculateTimeInQueue(ticket.created_at)}</p>
              </div>
            </div>

            <div className="text-[10px] text-neutral-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>Created {formatDisplayDate(ticket.created_at)}</span>
            </div>
          </div>

          {/* Comments Section - Scrollable */}
          <div className="px-5 pt-4 pb-0">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Comments ({ticket.comment?.length || 0})
              </h4>
            </div>
            
            {/* Comments List - Compact and Scrollable */}
            <div className="space-y-2 mb-0 max-h-[400px] overflow-y-auto pr-2">
              {ticket.comment && ticket.comment.length > 0 ? (
                ticket.comment.map((comment, index) => (
                  <div key={index} className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                    <div className="flex items-start gap-2 mb-1.5">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#d4af37] to-[#b8941e] flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-[9px] font-semibold">
                          {comment._commentby 
                            ? `${comment._commentby.first_name?.[0] || ''}${comment._commentby.last_name?.[0] || ''}`
                            : 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-xs text-neutral-900">
                          {comment._commentby 
                            ? `${comment._commentby.first_name || ''} ${comment._commentby.last_name || ''}`
                            : 'Unknown User'}
                        </p>
                        <p className="text-[10px] text-neutral-500">{formatDisplayDate(comment.created_at)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap pl-8">
                      {comment.message || 'No comment text'}
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 bg-neutral-50 rounded-lg border border-neutral-100">
                  <MessageSquare className="w-6 h-6 text-neutral-300 mx-auto mb-1" />
                  <p className="text-[10px] text-neutral-400">No comments yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Comment - Fixed at Bottom - Only show if user can comment */}
        {canComment ? (
          <div className="border-t border-neutral-200 px-4 py-2 pb-[10vh] bg-white">
            <textarea
              placeholder="Write your comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none text-xs focus:outline-none focus:ring-2 focus:ring-[#d4af37]/20 focus:border-[#d4af37] placeholder:text-neutral-400"
              rows={2}
            />
            <div className="flex justify-end mt-1.5">
              <Button
                onClick={handleAddComment}
                disabled={addingComment || !newComment.trim()}
                className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-lg text-xs h-7 px-3"
              >
                {addingComment ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-3 h-3 mr-1" />
                    Post Comment
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t border-neutral-200 px-4 py-3 pb-[10vh] bg-neutral-50">
            <div className="flex items-center gap-2 text-neutral-500 text-xs">
              <MessageSquare className="w-4 h-4" />
              <span>This ticket is not assigned to you. You can view but cannot add comments.</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Assign Ticket Modal Component
function AssignTicketModal({ ticket, onClose, onAssign }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);

  // Fetch staff list from API
  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        setLoadingStaff(true);
        const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/support/stafflist');
        setStaffList(response.data || []);
      } catch (err) {
        console.error('Error fetching staff list:', err);
        toast.error('Failed to load staff list');
      } finally {
        setLoadingStaff(false);
      }
    };
    
    fetchStaffList();
  }, []);

  const handleAssign = async () => {
    if (!selectedUser) {
      toast.error('Please select a team member');
      return;
    }

    setAssigning(true);
    try {
      await onAssign(selectedUser);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl z-50 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">Assign Ticket</h2>
          <p className="text-sm text-neutral-500 mt-1">Select a team member to assign this ticket</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Ticket Info */}
          <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Ticket</p>
            <p className="text-sm font-medium text-neutral-900">{ticket.name || 'Untitled Ticket'}</p>
          </div>

          {/* Team Member Selection */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Assign To
            </label>
            {loadingStaff ? (
              <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 flex items-center gap-2 text-sm text-neutral-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading staff...
              </div>
            ) : (
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]/20 focus:border-[#d4af37] text-sm"
                disabled={staffList.length === 0}
              >
                <option value="">Select team member...</option>
                {staffList.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.first_name} {staff.last_name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={assigning}
            className="rounded-lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={assigning || !selectedUser}
            className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-lg"
          >
            {assigning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign Ticket'
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

// Edit Ticket Modal Component
function EditTicketModal({ ticket, userInfo, onClose, onSave }) {
  const [priority, setPriority] = useState(ticket.priority || 'low');
  const [createdById, setCreatedById] = useState(ticket.createdBy_id || '');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Check if user has Admin permission
  const userPermissionsArray = userInfo?.permissions || [];
  const userPermissions = userPermissionsArray.map(item => 
    item?.user_permission?.permission || item?.permission
  ).filter(Boolean);
  const hasAdminPermission = userPermissions.includes('Admin');

  // Fetch internal users for Created By dropdown (only for admins)
  useEffect(() => {
    if (!hasAdminPermission) {
      setLoadingUsers(false);
      return;
    }
    
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/users');
        setUsers(response.data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    
    fetchUsers();
  }, [hasAdminPermission]);

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchLower = userSearch.toLowerCase();
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const npn = user.agent_npn?.toString().toLowerCase() || '';
    return fullName.includes(searchLower) || npn.includes(searchLower);
  });

  const selectedUser = users.find(u => u.id === createdById);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        priority,
        createdBy_id: createdById
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl z-50 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">Edit Ticket</h2>
          <p className="text-sm text-neutral-500 mt-1">Update ticket details</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Ticket Info */}
          <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Ticket</p>
            <p className="text-sm font-medium text-neutral-900">{ticket.name || 'Untitled Ticket'}</p>
          </div>

          {/* Priority Selection */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Priority
            </label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Created By Selection - Only editable for Admin */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Created By
            </label>
            {hasAdminPermission ? (
              loadingUsers ? (
                <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 flex items-center gap-2 text-sm text-neutral-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading users...
                </div>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between text-sm h-10"
                    >
                      {selectedUser 
                        ? `${selectedUser.first_name} ${selectedUser.last_name} - ${selectedUser.agent_npn}`
                        : "Select user..."}
                      <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <div className="flex items-center border-b px-3">
                        <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        <input
                          className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Search users..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto p-1">
                        {filteredUsers.length === 0 ? (
                          <div className="py-6 text-center text-sm text-neutral-500">
                            No users found.
                          </div>
                        ) : (
                          filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              onClick={() => {
                                setCreatedById(user.id);
                                setUserSearch('');
                              }}
                              className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 ${
                                createdById === user.id ? 'bg-neutral-100' : ''
                              }`}
                            >
                              <span className="flex-1 text-left">
                                {user.first_name} {user.last_name} - {user.agent_npn}
                              </span>
                              {createdById === user.id && (
                                <svg className="h-4 w-4 text-neutral-900" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </Command>
                  </PopoverContent>
                </Popover>
              )
            ) : (
              <div className="w-full px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-50 text-sm text-neutral-600">
                {ticket.createdBy_name || 'N/A'}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loadingUsers}
            className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-lg"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </>
  );
}



// Reassign Ticket Modal Component
function ReassignTicketModal({ ticket, onClose, onReassign }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [comment, setComment] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [reassigning, setReassigning] = useState(false);

  // Fetch staff list from API
  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        setLoadingStaff(true);
        const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/support/stafflist');
        setStaffList(response.data || []);
      } catch (err) {
        console.error('Error fetching staff list:', err);
        toast.error('Failed to load staff list');
      } finally {
        setLoadingStaff(false);
      }
    };
    
    fetchStaffList();
  }, []);

  const handleReassign = async () => {
    if (!selectedUser) {
      toast.error('Please select a team member');
      return;
    }
    
    if (!comment.trim()) {
      toast.error('Please add a comment about this reassignment');
      return;
    }

    setReassigning(true);
    try {
      await onReassign(selectedUser, comment);
    } finally {
      setReassigning(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl z-50 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">Reassign Ticket</h2>
          <p className="text-sm text-neutral-500 mt-1">Reassign this ticket to another team member</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Ticket Info */}
          <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Ticket</p>
            <p className="text-sm font-medium text-neutral-900">{ticket.name || 'Untitled Ticket'}</p>
            {ticket.assignedTo_name && (
              <p className="text-xs text-neutral-500 mt-1">Currently assigned to: <span className="font-medium">{ticket.assignedTo_name}</span></p>
            )}
          </div>

          {/* Reassign To Selection */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Reassign To <span className="text-red-500">*</span>
            </label>
            {loadingStaff ? (
              <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 flex items-center gap-2 text-sm text-neutral-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading staff...
              </div>
            ) : (
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37]/20 focus:border-[#d4af37] text-sm"
                disabled={staffList.length === 0}
              >
                <option value="">Select team member...</option>
                {staffList.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.first_name} {staff.last_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Comment Field */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Add Comment <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment about this reassignment..."
              className="w-full min-h-[100px] text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={reassigning}
            className="rounded-lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handleReassign}
            disabled={reassigning || !selectedUser || !comment.trim()}
            className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-lg"
          >
            {reassigning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Reassigning...
              </>
            ) : (
              'Reassign Ticket'
            )}
          </Button>
        </div>
      </div>
    </>
  );
}



// Add Ticket Modal Component
function AddTicketModal({ userInfo, onClose, onAdd }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [createdById, setCreatedById] = useState('');
  const [priority, setPriority] = useState('low');
  const [status, setStatus] = useState('to do');
  const [assignToId, setAssignToId] = useState('');
  
  const [users, setUsers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [adding, setAdding] = useState(false);
  
  const [userSearch, setUserSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');

  // Fetch internal users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/users');
        setUsers(response.data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch staff list
  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        setLoadingStaff(true);
        const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/support/stafflist');
        setStaffList(response.data || []);
      } catch (err) {
        console.error('Error fetching staff list:', err);
        toast.error('Failed to load staff list');
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchStaffList();
  }, []);

  // Filter users and staff based on search
  const filteredUsers = users.filter(user => {
    const searchLower = userSearch.toLowerCase();
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const npn = user.agent_npn?.toString().toLowerCase() || '';
    return fullName.includes(searchLower) || npn.includes(searchLower);
  });

  const filteredStaff = staffList.filter(staff => {
    const searchLower = staffSearch.toLowerCase();
    const fullName = `${staff.first_name} ${staff.last_name}`.toLowerCase();
    return fullName.includes(searchLower);
  });

  const selectedUser = users.find(u => u.id === createdById);
  const selectedStaff = staffList.find(s => s.id === assignToId);

  const handleAdd = async () => {
    if (!title.trim()) {
      toast.error('Please enter a ticket title');
      return;
    }

    if (!description.trim()) {
      toast.error('Please enter a ticket description');
      return;
    }

    if (!createdById) {
      toast.error('Please select who requested/created this ticket');
      return;
    }

    setAdding(true);
    try {
      await onAdd({
        title: title.trim(),
        description: description.trim(),
        createdBy_id: createdById,
        priority,
        status,
        assignTo_id: assignToId || null
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-2xl shadow-2xl z-50 animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">Add New Ticket</h2>
          <p className="text-sm text-neutral-500 mt-1">Create a support ticket</p>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Title */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter ticket title..."
              className="w-full"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">
              Description <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter ticket description..."
              className="w-full min-h-[100px] resize-none"
            />
          </div>

          {/* Two column grid for dropdowns */}
          <div className="grid grid-cols-2 gap-4">
            {/* Created By - Searchable */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Requested/Created By <span className="text-red-500">*</span>
              </label>
              {loadingUsers ? (
                <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 flex items-center gap-2 text-sm text-neutral-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between text-sm h-10"
                    >
                      {selectedUser 
                        ? `${selectedUser.first_name} ${selectedUser.last_name} - ${selectedUser.agent_npn}`
                        : "Select user..."}
                      <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <div className="flex items-center border-b px-3">
                        <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        <input
                          className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                          placeholder="Search users..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto p-1">
                        {filteredUsers.length === 0 ? (
                          <div className="py-6 text-center text-sm text-neutral-500">No users found.</div>
                        ) : (
                          filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              onClick={() => {
                                setCreatedById(user.id);
                                setUserSearch('');
                              }}
                              className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 ${
                                createdById === user.id ? 'bg-neutral-100' : ''
                              }`}
                            >
                              <span className="flex-1 text-left text-xs">
                                {user.first_name} {user.last_name} - {user.agent_npn}
                              </span>
                              {createdById === user.id && (
                                <svg className="h-4 w-4 text-neutral-900" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Assign To - Searchable */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Assign To
              </label>
              {loadingStaff ? (
                <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 flex items-center gap-2 text-sm text-neutral-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between text-sm h-10"
                    >
                      {selectedStaff 
                        ? `${selectedStaff.first_name} ${selectedStaff.last_name}`
                        : "Select staff..."}
                      <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <div className="flex items-center border-b px-3">
                        <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        <input
                          className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                          placeholder="Search staff..."
                          value={staffSearch}
                          onChange={(e) => setStaffSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto p-1">
                        {filteredStaff.length === 0 ? (
                          <div className="py-6 text-center text-sm text-neutral-500">No staff found.</div>
                        ) : (
                          filteredStaff.map((staff) => (
                            <button
                              key={staff.id}
                              onClick={() => {
                                setAssignToId(staff.id);
                                setStaffSearch('');
                              }}
                              className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 ${
                                assignToId === staff.id ? 'bg-neutral-100' : ''
                              }`}
                            >
                              <span className="flex-1 text-left">
                                {staff.first_name} {staff.last_name}
                              </span>
                              {assignToId === staff.id && (
                                <svg className="h-4 w-4 text-neutral-900" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* Priority and Status */}
          <div className="grid grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Priority <span className="text-red-500">*</span>
              </label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Status <span className="text-red-500">*</span>
              </label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="to do">To Do</SelectItem>
                  <SelectItem value="in progress">In Progress</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={adding}
            className="rounded-lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={adding || !title.trim() || !description.trim() || !createdById}
            className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-lg"
          >
            {adding ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Ticket
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

